import React, { useMemo } from "react";
import { Mail } from "lucide-react";
import Modal from "../../components/common/Modal";
import Typography from "../../components/common/Typography";
import Button from "../../components/common/Button";
import CustomInput from "../../components/common/CustomInput";
import type { Organisation, InvitableRole } from "../../types/types";
import { useInviteUserDialogHook } from "./useInviteUserDialogHook";

const ROLE_OPTIONS: Record<string, { value: InvitableRole; label: string }[]> = {
  super_admin: [
    { value: "learner", label: "Learner" },
    { value: "admin", label: "Admin" },
    { value: "organisation_admin", label: "Organisation Admin" },
    { value: "platform_admin", label: "Platform Admin" },
    { value: "billing_admin", label: "Billing Admin" },
  ],
  organisation_admin: [
    { value: "organisation_admin", label: "Organisation Admin" },
    { value: "admin", label: "Admin" },
    { value: "learner", label: "Learner" },
    { value: "platform_admin", label: "Platform Admin" },
    { value: "billing_admin", label: "Billing Admin" },
  ],
  admin: [
    { value: "organisation_admin", label: "Organisation Admin" },
    { value: "admin", label: "Admin" },
    { value: "learner", label: "Learner" },
    { value: "platform_admin", label: "Platform Admin" },
    { value: "billing_admin", label: "Billing Admin" },
  ],
  platform_admin: [
    { value: "learner", label: "Learner" },
    { value: "platform_admin", label: "Platform Admin" },
  ],
};

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  organisation: Organisation | null;
  onSubmit: (userData: {
    email: string;
    name: string;
    role: InvitableRole;
    jobTitle?: string;
    buyer_type?: string;
  }) => Promise<void>;
  loading: boolean;
  actorRole?: string;
}

const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  open,
  onClose,
  organisation,
  onSubmit,
  loading,
  actorRole,
}) => {
  const {
    formData,
    emailError,
    handleEmailChange,
    handleNameChange,
    handleRoleChange,
    handleJobTitleChange,
    handleDecisionMakerChange,
    handleSubmit,
    handleClose,
    isFormValid,
  } = useInviteUserDialogHook(onSubmit, onClose, loading);

  const roleOptions = useMemo(
    () =>
      ROLE_OPTIONS[actorRole?.toLowerCase() ?? ""] ?? [
        { value: "learner" as InvitableRole, label: "Learner" },
      ],
    [actorRole]
  );

  const actions = (
    <div className="flex justify-end gap-3 w-full">
      <Button onClick={handleClose} variant="outline" label="Cancel" />
      <Button
        onClick={handleSubmit}
        disabled={!isFormValid}
        variant="filled"
        label={loading ? "Inviting..." : "Invite"}
      />
    </div>
  );

  return (
    <Modal
      activeModal={open}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary rounded-lg flex-shrink-0">
            <Mail className="text-white" size={20} />
          </div>
          <div className="flex-1">
            <Typography variant="h5" weight="semibold" className="!text-textPrimary">
              Invite User
            </Typography>
            {organisation?.name && (
              <Typography variant="small" className="!text-textPrimary-grey">
                {organisation.name}
              </Typography>
            )}
          </div>
        </div>
      }
      modalSize="medium"
      bodyClass="p-6"
      dialogClass="!w-[calc(100vw-2rem)] max-w-[600px]"
      footerContent={actions}
    >
      <div className="space-y-8 w-full">
        <CustomInput
          label="Full Name"
          value={formData.name}
          onChange={handleNameChange}
          required
          className={{
            container: "w-full",
            input: "w-full text-base",
          }}
        />
        <CustomInput
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={handleEmailChange}
          error={emailError}
          required
          className={{
            container: "w-full",
            input: "w-full text-base",
          }}
        />
        <CustomInput
          label="Job Title"
          value={formData.jobTitle}
          onChange={handleJobTitleChange}
          placeholder="e.g., Marketing Manager, Teacher"
          className={{
            container: "w-full",
            input: "w-full text-base",
          }}
        />
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Role</label>
          <select
            value={formData.role}
            onChange={handleRoleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[rgb(var(--pd-focus))]"
          >
            {roleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {/* WS-O P1.1: invite the trial's purchasing decision-maker so they can
            buy. Overrides the role to organisation_admin + sets the buyer_type. */}
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isDecisionMaker}
            onChange={handleDecisionMakerChange}
            className="mt-1"
          />
          <span className="text-sm text-gray-700">
            Invite as our purchasing decision-maker — gives them admin access and
            the ability to purchase the rollout (overrides the role above).
          </span>
        </label>
      </div>
    </Modal>
  );
};

export default InviteUserDialog;
